import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useLocation } from "react-router-dom";
import { Column, Row } from "@components/Flex";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import Button from "@components/Button";
import Loading from "@components/Loading";
import Tooltip from "@components/Tooltip";
import Switch from "@components/Switch";
import { ReactComponent as InfoIcon } from "@assets/icons/info.svg";
import { useLang } from "@src/hooks/useLang";
import {
  getJohnDeereStatus,
  getJohnDeereConnectUrl,
  getJohnDeereOrganizations,
  disconnectJohnDeere,
  getJohnDeereSyncSettings,
  putJohnDeereSyncSettings,
} from "@services/johnDeereService";
import { useIntegrationSyncSettings, SyncUnitsFields } from "@screens/ProfileScreen/IntegrationSyncSettings";
import IntegrationCard, { CollapsedSetup } from "@screens/ProfileScreen/IntegrationCard";
import { observer } from "mobx-react-lite";

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AUTO_SYNC_HINT =
  "Your harvested fields sync to John Deere Operations Center automatically — once a day when the yield or calibration changes, and once more when you mark a field complete. Turn this off to sync only manually.";

const SYNC_UNITS_HINT =
  "Units used for the yield maps shown in John Deere Operations Center. This is set for the whole account — team members and automatic syncs all use it.";

// The connections URL comes from the backend; only ever open John Deere's own domain.
const sanitizeConnectionsUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (parsed.protocol !== "https:") return null;
    if (host !== "deere.com" && !host.endsWith(".deere.com")) return null;
    return parsed.toString();
  } catch {
    return null;
  }
};

const JohnDeereProfileSection: React.FC = () => {
  const lang = useLang();
  const location = useLocation();
  const jdLang = (lang as any).johnDeere ?? {};
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  // Setup form is collapsed until the user asks for it — see the disconnected
  // branch below.
  const [setupOpen, setSetupOpen] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionRequired, setConnectionRequired] = useState(false);
  const [connectionsUrl, setConnectionsUrl] = useState<string | null>(null);
  const sync = useIntegrationSyncSettings(connected, getJohnDeereSyncSettings, putJohnDeereSyncSettings);

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const status = await getJohnDeereStatus();
      setConnected(status.connected);
    } catch (e: any) {
      setConnected(false);
      const msg = e?.message ?? "Failed to check status";
      setError(msg);
      if (typeof window !== "undefined") {
        console.warn("[John Deere] Status failed:", msg);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  // Refetch when returning from John Deere OAuth callback (?deere_connected=1)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("deere_connected") === "1") {
      fetchStatus();
    }
  }, [location.search]);

  // When connected, check if org connection is needed (connections.deere.com)
  useEffect(() => {
    if (!connected) {
      setConnectionRequired(false);
      setConnectionsUrl(null);
      return;
    }
    getJohnDeereOrganizations()
      .then((r) => {
        setConnectionRequired(r.connectionRequired ?? false);
        setConnectionsUrl(sanitizeConnectionsUrl(r.connectionsUrl));
      })
      .catch(() => {
        setConnectionRequired(false);
        setConnectionsUrl(null);
      });
  }, [connected]);

  const handleConnect = () => {
    window.location.assign(getJohnDeereConnectUrl());
  };

  const handleDisconnect = async () => {
    setDisconnectLoading(true);
    try {
      await disconnectJohnDeere();
      setConnected(false);
    } finally {
      setDisconnectLoading(false);
    }
  };

  const title = jdLang.integrationTitle ?? "John Deere Operations Center";

  if (loading) {
    return (
      <IntegrationCard title={title}>
        <Loading />
      </IntegrationCard>
    );
  }

  // Disconnected and not being set up: one line and a button. The permissions
  // walkthrough below is only useful to someone actually connecting — a
  // Cropwise user shouldn't scroll past it on every visit to Settings.
  if (!connected && !setupOpen) {
    return (
      <IntegrationCard
        title={title}
        connected={false}
        disconnectedLabel={(jdLang as any).statusNotConnected ?? "Not connected"}
      >
        {error && (
          <>
            <Text size={14} color="#dc2626">
              {error}
            </Text>
            <SizedBox height={8} />
          </>
        )}
        <CollapsedSetup
          description={
            (jdLang as any).collapsedDescription ??
            "Import field boundaries from Operations Center and send yield maps back."
          }
          buttonLabel={jdLang.connectButton ?? "Connect John Deere"}
          onExpand={() => setSetupOpen(true)}
        />
      </IntegrationCard>
    );
  }

  return (
    <IntegrationCard
      title={title}
      connected={connected}
      connectedLabel={(jdLang as any).statusConnected ?? "Connected"}
      disconnectedLabel={(jdLang as any).statusNotConnected ?? "Not connected"}
    >
      {error && (
        <>
          <Text size={14} color="#dc2626">
            {error}
          </Text>
          <SizedBox height={8} />
        </>
      )}
      {connected ? (
        <Column crossAxisSize="max" style={{ gap: 12 }}>
          <ToggleRow>
            <Text size={14} color="#374151">
              {jdLang.autoSyncLabel ?? "Automatic sync"}
            </Text>
            <Tooltip
              content={
                <Text size={12} style={{ maxWidth: 260, display: "block" }}>
                  {jdLang.autoSyncHint ?? AUTO_SYNC_HINT}
                </Text>
              }
            >
              <InfoIcon />
            </Tooltip>
            <div style={{ flex: 1 }} />
            <Switch
              on={sync.settings.autoSyncYields}
              disabled={!sync.loaded || sync.saving}
              onToggle={() => sync.save({ autoSyncYields: !sync.settings.autoSyncYields })}
              ariaLabel="Toggle automatic John Deere sync"
            />
          </ToggleRow>
          <div>
            <ToggleRow>
              <Text size={14} color="#374151">
                {jdLang.syncUnitsLabel ?? "Units sent to John Deere"}
              </Text>
              <Tooltip
                content={
                  <Text size={12} style={{ maxWidth: 260, display: "block" }}>
                    {jdLang.syncUnitsHint ?? SYNC_UNITS_HINT}
                  </Text>
                }
              >
                <InfoIcon />
              </Tooltip>
            </ToggleRow>
            <SizedBox height={8} />
            <SyncUnitsFields
              settings={sync.settings}
              disabled={!sync.loaded || sync.saving}
              onChange={sync.save}
            />
          </div>
          {connectionRequired && connectionsUrl && (
            <div
              style={{
                background: "#EFF6FF",
                border: "1px solid #3B82F6",
                borderRadius: 8,
                padding: 16,
              }}
            >
              <Text size={13} color="#1E40AF" style={{ display: "block", marginBottom: 8 }}>
                Grant <strong>Organization</strong>, <strong>Locations</strong>, and <strong>Work</strong> access to import fields and upload prescriptions.
              </Text>
              <Text size={12} color="#4B5563" style={{ display: "block", marginBottom: 6 }}>
                At connections.deere.com or Team Manager, expand <strong>Organization</strong> and check:
              </Text>
              <Text size={12} color="#4B5563" style={{ display: "block", marginBottom: 6, paddingLeft: 12 }}>
                ✅ <strong>"Share Organization Data with Connected Software Companies"</strong> — required for Green Growth to access your farms, fields, and boundaries.
              </Text>
              <Text size={12} color="#4B5563" style={{ display: "block", marginBottom: 6, paddingLeft: 12 }}>
                ✅ <strong>Work → Manage</strong> — required for uploading prescriptions.
              </Text>
              <Text size={12} color="#4B5563" style={{ display: "block", marginBottom: 16 }}>
                After granting, <strong>disconnect and re-connect</strong> John Deere above to refresh permissions.
              </Text>
              <Button
                onClick={() => window.open(connectionsUrl, "_blank", "noopener,noreferrer")}
                style={{ maxWidth: 320 }}
              >
                Grant permissions at connections.deere.com →
              </Button>
            </div>
          )}
          <Button
            kind="outline"
            onClick={handleDisconnect}
            disabled={disconnectLoading}
            style={{ maxWidth: 160 }}
          >
            {disconnectLoading ? <Loading /> : (jdLang.disconnectButton ?? "Disconnect")}
          </Button>
        </Column>
      ) : (
        <Column crossAxisSize="max" style={{ gap: 12 }}>
          <div
            style={{
              background: "#F0FDF4",
              border: "1px solid #86EFAC",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <Text size={13} color="#166534" style={{ display: "block", marginBottom: 6 }}>
              {jdLang.connectPermissionHint ??
                "Required permissions for fields and prescriptions:"}
            </Text>
            <ul style={{ margin: 0, paddingLeft: 18, color: "#15803d", fontSize: 12 }}>
              <li>
                <strong>Organization</strong> — farms, fields, boundaries
              </li>
              <li>
                <strong>Locations</strong> — field locations
              </li>
              <li>
                <strong>Work</strong> (optional) — prescription map upload
              </li>
            </ul>
            <Text size={12} color="#15803d" style={{ marginTop: 6 }}>
              {jdLang.connectPermissionNote ??
                "At connections.deere.com, expand each category and grant access. Locations alone is not enough."}
            </Text>
          </div>
          <Row style={{ gap: 8 }}>
            <Button onClick={handleConnect} style={{ maxWidth: 200 }}>
              {jdLang.connectButton ?? "Connect John Deere"}
            </Button>
            <Button
              kind="outline"
              onClick={() => setSetupOpen(false)}
              style={{ maxWidth: 120 }}
            >
              {jdLang.cancel ?? "Cancel"}
            </Button>
          </Row>
        </Column>
      )}
    </IntegrationCard>
  );
};

export default observer(JohnDeereProfileSection);
